// /docs/how-it-works — architecture story at the capability level.
// Audience-appropriate capability language only.
// Three simple SVG block diagrams live under /public/docs/diagrams/.
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const STAGES = [
    {
        title: "1 · Assemble",
        body:
            "The operator composes a scenario from validated building blocks " +
            "— airframe, environment, terrain, threats, guidance profile, and " +
            "engagement. Each block carries its own provenance (source, confidence, " +
            "citation) that follows the block wherever it is used.",
    },
    {
        title: "2 · Validate",
        body:
            "A shared validation gate reads the assembled scenario and emits " +
            "blocking errors and warnings before any run is allowed to start. " +
            "Rules span every capability: a misfuzed warhead, a non-monotonic " +
            "Pk curve, a visibility mismatch against the chosen seeker, or a " +
            "directed-energy warhead without terrain all fail fast.",
    },
    {
        title: "3 · Run",
        body:
            "A deterministic Monte Carlo engine drives either the JSBSim-backed " +
            "6-DoF dynamics or the kinematic backend. Seeds are explicit; " +
            "same inputs produce byte-equal outputs. At run start, an immutable " +
            "snapshot of every parameter used is captured and persisted.",
    },
    {
        title: "4 · Analyse",
        body:
            "The run detail page shows miss statistics, detection and " +
            "lock-loss events, a guidance comparison chart, and — when an " +
            "engagement is attached — an overlaid p(kill) curve with a " +
            "Best-Profile summary. A full report can be exported as PDF or DOCX.",
    },
    {
        title: "5 · Review",
        body:
            "Analysts submit a successful run for review. A reviewer can " +
            "approve, request changes with notes (the run returns to the " +
            "analyst's queue), or reject outright. Only approved runs are " +
            "eligible to be promoted to golden references for later comparisons.",
    },
];

export default function DocsHowItWorksPage() {
    return (
        <>
            <SeoHead path="/docs/how-it-works"
                     title="UAVSimNet Docs — How it works"
                     description="Deterministic Monte Carlo, JSBSim-backed dynamics, validation gate, immutable parameter-provenance snapshot, and reviewer workflow." />
            <div data-testid="docs-how-it-works-page">
                <section className="section" style={{ paddingTop: 64 }}>
                    <div className="section__inner">
                        <span className="section__eyebrow">// DOCS · 2 OF 4</span>
                        <h1 className="section__heading">How it works</h1>
                        <p className="section__lede">
                            Five stages, deterministic end to end. Everything the
                            platform writes is reproducible from the same inputs,
                            and everything it claims is traceable back to a cited
                            source.
                        </p>

                        <figure
                            data-testid="docs-how-hero"
                            style={{
                                marginTop: 32,
                                padding: 16,
                                border: "1px solid var(--ux-border-1)",
                                borderRadius: 8,
                                background: "var(--ux-bg-soft, #11181f)",
                            }}
                        >
                            <img
                                src="/docs/diagrams/pipeline_block.svg"
                                alt="Block diagram of the five-stage pipeline: Assemble → Validate → Run → Analyse → Review."
                                style={{ width: "100%", borderRadius: 6, display: "block" }}
                            />
                            <figcaption
                                style={{
                                    marginTop: 10,
                                    color: "var(--ux-text-3)",
                                    fontSize: 12,
                                    fontFamily: "'JetBrains Mono', monospace",
                                }}
                            >
                                Five-stage pipeline: Assemble → Validate → Run →
                                Analyse → Review.
                            </figcaption>
                        </figure>

                        <ol
                            style={{
                                listStyle: "none",
                                padding: 0,
                                margin: "44px 0 0 0",
                                display: "grid",
                                gap: 18,
                            }}
                            data-testid="docs-how-stages"
                        >
                            {STAGES.map((s) => (
                                <li
                                    key={s.title}
                                    className="cap-card"
                                    data-testid={`docs-how-stage-${s.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                >
                                    <div
                                        style={{
                                            fontFamily:
                                                "'JetBrains Mono', monospace",
                                            color: "var(--ux-accent)",
                                            fontSize: 12,
                                            marginBottom: 6,
                                        }}
                                    >
                                        {s.title}
                                    </div>
                                    <p
                                        style={{
                                            color: "var(--ux-text-2)",
                                            fontSize: 14,
                                            lineHeight: 1.6,
                                            margin: 0,
                                        }}
                                    >
                                        {s.body}
                                    </p>
                                </li>
                            ))}
                        </ol>

                        <figure
                            data-testid="docs-provenance-diagram"
                            style={{
                                marginTop: 44,
                                padding: 16,
                                border: "1px solid var(--ux-border-1)",
                                borderRadius: 8,
                                background: "var(--ux-bg-soft, #11181f)",
                            }}
                        >
                            <img
                                src="/docs/diagrams/provenance_block.svg"
                                alt="Diagram showing how each parameter carries source, confidence, and citation through the scenario into the immutable run snapshot."
                                style={{ width: "100%", borderRadius: 6, display: "block" }}
                            />
                            <figcaption
                                style={{
                                    marginTop: 10,
                                    color: "var(--ux-text-3)",
                                    fontSize: 12,
                                    fontFamily: "'JetBrains Mono', monospace",
                                }}
                            >
                                Every parameter carries its source, confidence,
                                and citation into the immutable run snapshot.
                            </figcaption>
                        </figure>

                        <div style={{ marginTop: 48, display: "flex", gap: 14, flexWrap: "wrap" }}>
                            <Link to="/docs/evidence" className="btn-primary" data-testid="docs-how-next">
                                Next · Evidence <ArrowRight size={14} />
                            </Link>
                            <Link to="/docs" className="btn-ghost" data-testid="docs-how-back">
                                Back to docs index
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}
